import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { IonSpinner, createGesture, type Gesture } from '@ionic/react';

// Custom pull-to-refresh shared by the pull gesture AND the top-bar logo tap
// (see HomePage). Ionic's IonRefresher only starts from its own touch gesture,
// so it can't be driven from a button — this reimplements the same
// "content slides down → spinner shows → springs back" feel through a single
// code path (`runRefresh`) that both entry points call.
//
// The push-down is done by animating a top *spacer's* height rather than a CSS
// transform on the content: a transform would establish a new containing block
// and can detach `position: sticky` children (the feed's sticky filter bar),
// whereas growing a spacer just reflows the feed downward and leaves sticky
// intact.

const THRESHOLD = 64; // px of pull needed to commit to a refresh on release
const REFRESH_HEIGHT = 56; // px the spacer holds open while refreshing
const MAX_PULL = 96; // hard cap on how far the drag can stretch
const RESISTANCE = 0.5; // drag-to-offset damping so the pull feels rubbery
const MIN_SPINNER_MS = 700; // floor so a warm-cache refetch still reads as a refresh

export interface PullToRefreshHandle {
  /** Programmatically run the same refresh the pull gesture triggers. */
  trigger: () => void;
}

interface PullToRefreshProps {
  /** The enclosing IonContent — its scroll element hosts the gesture and tells
   *  us when we're at the top (the only place a pull should engage). */
  contentRef: RefObject<HTMLIonContentElement | null>;
  /** The actual refresh work (e.g. a TanStack Query refetch). */
  onRefresh: () => Promise<unknown>;
  children: ReactNode;
}

export const PullToRefresh = forwardRef<PullToRefreshHandle, PullToRefreshProps>(
  ({ contentRef, onRefresh, children }, ref) => {
    const [offset, setOffset] = useState(0);
    const [dragging, setDragging] = useState(false);
    // Ref mirror of the refreshing flag so the gesture callbacks (created once)
    // always see the live value without re-binding the gesture.
    const refreshingRef = useRef(false);

    const runRefresh = useCallback(async () => {
      if (refreshingRef.current) return;
      refreshingRef.current = true;
      setDragging(false);
      setOffset(REFRESH_HEIGHT);
      const started = performance.now();
      try {
        await onRefresh();
      } catch {
        // A failed refresh still has to release the spinner — leaving it
        // spinning forever reads as a hung app, and the gesture would stay
        // armed-off because `refreshingRef` never clears. Errors themselves
        // are surfaced by the query layer (global error modal / stale data
        // stays on screen), so there's nothing to re-raise here.
      } finally {
        const elapsed = performance.now() - started;
        const wait = Math.max(0, MIN_SPINNER_MS - elapsed);
        setTimeout(() => {
          setOffset(0);
          refreshingRef.current = false;
        }, wait);
      }
    }, [onRefresh]);

    useImperativeHandle(
      ref,
      () => ({
        trigger: () => {
          contentRef.current?.scrollToTop(300);
          runRefresh();
        },
      }),
      [contentRef, runRefresh],
    );

    useEffect(() => {
      const content = contentRef.current;
      if (!content) return;
      let gesture: Gesture | undefined;
      let cancelled = false;
      let pulled = 0;

      content.getScrollElement().then((scrollEl) => {
        if (cancelled) return;
        gesture = createGesture({
          el: scrollEl,
          gestureName: 'feed-pull-refresh',
          direction: 'y',
          // Only arm at the very top and while not already refreshing; anywhere
          // else the native scroll owns the gesture.
          canStart: () => !refreshingRef.current && scrollEl.scrollTop <= 0,
          onStart: () => setDragging(true),
          onMove: (d) => {
            // Downward pull only; an upward move means the user is scrolling the
            // list, so release back to rest and let native scroll take over.
            if (d.deltaY <= 0) {
              pulled = 0;
              setOffset(0);
              return;
            }
            pulled = Math.min(d.deltaY * RESISTANCE, MAX_PULL);
            setOffset(pulled);
          },
          onEnd: () => {
            setDragging(false);
            if (pulled >= THRESHOLD) {
              runRefresh();
            } else {
              setOffset(0);
            }
            pulled = 0;
          },
        });
        gesture.enable(true);
      });

      return () => {
        cancelled = true;
        gesture?.destroy();
      };
    }, [contentRef, runRefresh]);

    // Spinner grows/fades in as the pull approaches the commit threshold.
    const progress = Math.min(1, offset / THRESHOLD);

    return (
      <div className="relative">
        <div
          aria-hidden={offset === 0}
          className="flex items-center justify-center overflow-hidden"
          style={{
            height: offset,
            // Follow the finger 1:1 while dragging; ease when we snap open or
            // spring back so both the pull-release and the logo tap animate.
            transition: dragging ? 'none' : 'height 0.3s ease',
          }}
        >
          <IonSpinner
            name="crescent"
            className="text-brand-primary"
            style={{ opacity: progress, transform: `scale(${0.6 + progress * 0.4})` }}
          />
        </div>
        {children}
      </div>
    );
  },
);

PullToRefresh.displayName = 'PullToRefresh';
